import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { AdIntelligenceEnumsPlacementType } from "@similarweb/pro-api-client";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import { EErrorTypes, GenericEmptyState } from "app/components/React/empty-state/GenericEmptyState";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useDebounce } from "app/custom-hooks/useDebounce";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
// eslint-disable-next-line max-len
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { buildHeatMapFromEvents } from "../../shared/heatmap/heatmap-events";

import { useVerification } from "./useVerification";
import { useVerificationCampaignsOptions, useVerificationPublishersOptions } from "./useVerificationFilterOptions";
import type { IVerificationFilterState } from "./verification-filters.types";
import {
    PUBLISHERS_PER_PAGE,
    type VerificationRecord,
    buildCampaignOptions,
    buildVerificationExcelUrl,
    buildFilterPublisherOptions,
    clampPage,
    getChangedFilterName,
    getMaxCellValue,
    getPageSliceAsEventsByRow,
    getScreenshotsCount,
    getTotalPages,
    sumRecordCounts,
} from "./verification-widget.utils";
import type { IVerificationCellData } from "./VerificationDrawer";
import { VerificationDrawer } from "./VerificationDrawer";
import { VerificationFiltersBar } from "./VerificationFiltersBar";
import { VerificationFooter } from "./VerificationFooter";
import { VerificationHeatMapView } from "./VerificationHeatMapView";

export const VerificationWidget = () => {
    const { translate } = useTranslate();
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const isCompare = chosenSites.isCompare();
    const selectedSite = params.verificationSelectedSite ?? params.key?.split(",")[0] ?? "";

    const filters: IVerificationFilterState = useMemo(() => params.dataFilters ?? {}, [params.dataFilters]);
    const onlyScreenshots = !!params.screenshotsOnly;

    const [currentPage, setCurrentPage] = useState(1);
    const [debouncedCampaignSearch, setCampaignSearch, campaignSearch] = useDebounce("");
    const [drawerCellData, setDrawerCellData] = useState<IVerificationCellData | null>(null);
    const setFilters = useCallback(
        (next: IVerificationFilterState) => {
            const changedFilter = getChangedFilterName(filters, next);
            if (changedFilter) {
                allTrackers.dispatchTrackEvent("Verification widget", "filter", changedFilter);
            }
            proNavigator.updateQueryParams({ dataFilters: next }, { reload: false });
            setCurrentPage(1);
        },
        [filters],
    );
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");

    const selectedPositions =
        filters.adPositions && filters.adPositions.length > 0
            ? (filters.adPositions as AdIntelligenceEnumsPlacementType[])
            : undefined;

    const selectedCampaigns = filters.campaigns && filters.campaigns.length > 0 ? filters.campaigns : undefined;
    const selectedPublishers = filters.publishers && filters.publishers.length > 0 ? filters.publishers : undefined;

    const {
        data: response,
        totals,
        isLoading,
        isError,
        totalDays,
        dateFrom,
        dateTo,
    } = useVerification({
        advertiser: selectedSite,
        position: selectedPositions,
        campaignName: selectedCampaigns,
        publishers: selectedPublishers,
        page: currentPage,
        pageSize: PUBLISHERS_PER_PAGE,
        screenshotFilter: onlyScreenshots ? "HasScreenshot" : "All",
    });

    const valueOf = onlyScreenshots ? getScreenshotsCount : sumRecordCounts;

    // BE returns the current page already filtered/paginated; FE just preserves the response order.
    const pagePublishers = useMemo(() => {
        const entries: Array<[string, VerificationRecord[]]> = Object.entries(response?.data ?? {}).filter(
            (entry): entry is [string, VerificationRecord[]] => entry[1] !== null,
        );
        const count = entries.reduce((acc, [, records]) => acc + valueOf(records), 0);
        return { items: entries, count };
    }, [response, valueOf]);

    const totalPublisherCount = totals?.totalPublisherCount ?? 0;
    const totalPages = getTotalPages(totalPublisherCount, PUBLISHERS_PER_PAGE);
    const safePage = clampPage(currentPage, totalPages);

    const eventsByRow = useMemo(() => {
        // Page slice is done server-side; sort the page rows by aggregated count (desc) so the
        // heatmap is ordered by activity rather than by publisher name.
        const sortedByCount = [...pagePublishers.items].sort(([, a], [, b]) => valueOf(b) - valueOf(a));
        return getPageSliceAsEventsByRow(sortedByCount, 1, PUBLISHERS_PER_PAGE, valueOf);
    }, [pagePublishers, valueOf]);

    const { data, lookup, labelEvery } = useMemo(
        () =>
            buildHeatMapFromEvents<VerificationRecord>(eventsByRow, {
                totalDays,
                dateFrom,
                dateTo,
                getDate: (r) => r.dateTime,
                getValue: valueOf,
            }),
        [eventsByRow, totalDays, dateFrom, dateTo, valueOf],
    );

    const rowTimesSeenTotals = useMemo(() => {
        if (!onlyScreenshots) {
            return null;
        }
        const perRowTotals: Record<string, number> = {};
        for (const [rowKey, records] of Object.entries(eventsByRow)) {
            perRowTotals[rowKey] = sumRecordCounts(records);
        }
        return perRowTotals;
    }, [eventsByRow, onlyScreenshots]);

    const { data: publishersResponse, isFetching: isPublishersLoading } = useVerificationPublishersOptions({
        advertiser: selectedSite,
    });

    const { data: campaignsResponse, isFetching: isCampaignsLoading } = useVerificationCampaignsOptions({
        advertiser: selectedSite,
        search: debouncedCampaignSearch,
    });

    const publisherOptions = useMemo(
        () => buildFilterPublisherOptions(publishersResponse?.publishers),
        [publishersResponse],
    );

    const campaignOptions = useMemo(
        () => buildCampaignOptions(campaignsResponse?.data?.map((item) => item.campaign)),
        [campaignsResponse],
    );

    const maxCellValue = useMemo(() => getMaxCellValue(data), [data]);

    const toggleOnlyScreenshots = useCallback(() => {
        allTrackers.dispatchTrackEvent("Verification widget", "toggle", "Screenshots toggle");
        proNavigator.applyUpdateParams({
            screenshotsOnly: !onlyScreenshots,
        });
        setCurrentPage(1);
    }, [onlyScreenshots]);

    const handleCellClick = useCallback((cell: IVerificationCellData) => {
        allTrackers.dispatchTrackEvent("Verification widget", "click", "Verification details");
        setDrawerCellData(cell);
    }, []);

    const handleDrawerOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setDrawerCellData(null);
        }
    }, []);

    const onSelectSite = useCallback((site: string) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${site}`);
        proNavigator.applyUpdateParams({
            verificationSelectedSite: site,
        });
    }, []);

    const handleExcelClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("Download", "click", "Verification/Excel");
    }, []);

    const excelUrl = useMemo(() => {
        if (!selectedSite || !dateFrom || !dateTo) {
            return undefined;
        }
        return buildVerificationExcelUrl({
            advertiser: selectedSite,
            country: params.country,
            dateFrom,
            dateTo,
            device: "All",
            position: selectedPositions,
            campaignName: selectedCampaigns,
            publishers: selectedPublishers,
            screenshotFilter: onlyScreenshots ? "HasScreenshot" : "All",
        });
    }, [
        selectedSite,
        params.country,
        dateFrom,
        dateTo,
        selectedPositions,
        selectedCampaigns,
        selectedPublishers,
        onlyScreenshots,
    ]);

    return (
        <WidgetContainer
            className="col-xs"
            title={translate("ad.intelligence.verification.widget.title")}
            tooltip={translate("ad.intelligence.verification.widget.tooltip")}
            subtitleFilters={subtitleFilters}
            excelUrl={totalPublisherCount > 0 ? excelUrl : undefined}
            onExcelClick={handleExcelClick}
            isExcelDisabled={!swSettings.current.resources.IsExcelAllowed}
            totalCount={totalPublisherCount}
            hasPaidExcel
            withBorder
        >
            {isCompare ? (
                <CompareTabsContainer
                    onSelectSite={onSelectSite}
                    keyName="verificationOverviewFirstLoads"
                    domainQueryParamName="verificationSelectedSite"
                    hideResults={true}
                />
            ) : null}
            <Flex
                flexDirection="column"
                padding="16px 24px 24px"
            >
                <VerificationFiltersBar
                    filters={filters}
                    onFiltersChange={setFilters}
                    publisherOptions={publisherOptions}
                    campaignOptions={campaignOptions}
                    isPublishersLoading={isPublishersLoading}
                    isCampaignsLoading={isCampaignsLoading}
                    campaignSearch={campaignSearch}
                    onCampaignSearch={setCampaignSearch}
                    onlyScreenshots={onlyScreenshots}
                    onOnlyScreenshotsToggle={toggleOnlyScreenshots}
                />
                {!isLoading && !isError && totalPublisherCount === 0 ? (
                    <GenericEmptyState
                        title={translate("ad.intelligence.verification.widget.empty.title")}
                        subtitle={translate("ad.intelligence.verification.widget.empty.subtitle")}
                        type={EErrorTypes.NO_DATA}
                    />
                ) : (
                    <>
                        <VerificationHeatMapView
                            isFetching={isLoading}
                            data={data}
                            lookup={lookup}
                            labelEvery={labelEvery}
                            maxCellValue={maxCellValue}
                            page={safePage}
                            publishersCount={totalPublisherCount}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            onlyScreenshots={onlyScreenshots}
                            rowTimesSeenTotals={rowTimesSeenTotals}
                            onPageChange={setCurrentPage}
                            onDetailsClick={handleCellClick}
                        />
                        <VerificationFooter
                            isFetching={isLoading}
                            totalCount={totals?.totalCount ?? 0}
                            publishersCount={totalPublisherCount}
                        />
                    </>
                )}
            </Flex>
            <VerificationDrawer
                isOpen={drawerCellData !== null}
                onOpenChange={handleDrawerOpenChange}
                cellData={drawerCellData}
                advertiser={selectedSite}
                position={selectedPositions}
            />
        </WidgetContainer>
    );
};
